
import React from 'react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Recipe } from '@/types/recipe';
import { MealPlan } from '@/types/mealPlan';
import { MealCompositionWithRecipes } from '@/types/mealComposition';
import { MealNote } from '@/types/mealNote';
import { format, addDays, subDays } from 'date-fns';
import { Skeleton } from '@/components/ui/skeleton';
import HomepageTile from './HomepageTile';

interface UpcomingMealsProps {
  mealPlans: MealPlan[];
  recipes: Recipe[];
  mealCompositions: MealCompositionWithRecipes[];
  mealNotes: MealNote[];
  isLoading: boolean;
}

const UpcomingMeals: React.FC<UpcomingMealsProps> = ({ 
  mealPlans, 
  recipes, 
  mealCompositions,
  mealNotes,
  isLoading 
}) => {
  const today = new Date();
  const yesterday = subDays(today, 1);
  const tomorrow = addDays(today, 1);
  
  const todayFormatted = format(today, 'yyyy-MM-dd');
  const yesterdayFormatted = format(yesterday, 'yyyy-MM-dd');
  const tomorrowFormatted = format(tomorrow, 'yyyy-MM-dd');
  
  // Find meal plans for specific date
  const getMealPlansForDate = (dateString: string) => {
    return mealPlans.filter(mp => mp.date === dateString);
  };

  // Find meal notes for specific date
  const getMealNotesForDate = (dateString: string) => {
    return mealNotes.filter(note => note.date === dateString);
  };

  // Get meal plans for yesterday, today and tomorrow
  const yesterdayMealPlans = getMealPlansForDate(yesterdayFormatted);
  const todayMealPlans = getMealPlansForDate(todayFormatted);
  const tomorrowMealPlans = getMealPlansForDate(tomorrowFormatted);

  // Get meal notes for yesterday, today and tomorrow
  const yesterdayNotes = getMealNotesForDate(yesterdayFormatted);
  const todayNotes = getMealNotesForDate(todayFormatted);
  const tomorrowNotes = getMealNotesForDate(tomorrowFormatted);

  if (isLoading) {
    return (
      <div>
        <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-charcoal font-playfair mb-2 mt-2">Upcoming Meals</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {Array.from({ length: 2 }).map((_, index) => (
            <div key={index} className="space-y-2 p-4 border rounded-lg">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-8 w-full" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-charcoal font-playfair mb-2 mt-2">Upcoming Meals</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Today's meal */}
        <HomepageTile
          date={today}
          mealPlans={todayMealPlans}
          mealNotes={todayNotes}
          recipes={recipes}
          mealCompositions={mealCompositions}
        />
        
        {/* Tomorrow's meal */}
        <HomepageTile
          date={tomorrow}
          mealPlans={tomorrowMealPlans}
          mealNotes={tomorrowNotes}
          recipes={recipes}
          mealCompositions={mealCompositions}
        />
      </div>
      <div className="mt-4 flex justify-end">
        <Link to="/meal-planner">
          <Button variant="ghost" size="sm" className="flex items-center">
            View Full Meal Plan
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default UpcomingMeals;
